import React, { useCallback, useState } from 'react';
import { LessonListItem, LessonsList } from '@components/learning/LessonsList';
import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import { Icon, Heading } from '@components/shared';
import { svgCompletedGap } from './assets/completedGap';
import { useDeviceSize } from '@hooks/useDeviceSize';

const TABLET_SCREEN_WIDTH = 900;

export enum CourseStatus {
  /** The course is not started */
  DEFAULT = 'default',
  /** The course is in progress */
  IN_PROGRESS = 'in_progress',
  /** The course is finished */
  FINISHED = 'finished',
}

export interface CourseProps {
  /** The title of the course */
  title: string;
  /** The subtitle of the course */
  subTitle: string;
  /** The duration of the course */
  duration: string;
  /** The URL to navigate to when course is clicked */
  href: string;
  /** The course slug */
  slug: string;
  /** The image URL of the course */
  imageUrl: string;
  /** The status of the course */
  courseStatus: CourseStatus;
}

export interface CourseListProps {
  /** The course data */
  course: CourseProps;
  /** The lessons associated with the course */
  lessons: LessonListItem[];
  /** The function to call when a lesson element from the list is clicked */
  onLessonClick: (slug: string) => void;
  /** The function to call when the course card is clicked */
  onCourseTitleClick: () => void;
  /** The function to call when the course button is clicked */
  onCourseButtonClick?: () => void;
}

/**
 * Displays a course card with lessons list and progress tracking
 * @param {CourseListProps} CourseListProps
 * @returns {React.ReactElement} React.ReactElement
 *
 * @example
 * <CourseList
 *   course={courseData}
 *   lessons={lessonsData}
 *   onLessonClick={(slug) => navigate(`/lesson/${slug}`)}
 *   onCourseTitleClick={() => navigate(`/course/${course.slug}`)}
 * />
 */
export const CourseList = ({
  course,
  lessons,
  onLessonClick,
  onCourseTitleClick,
  onCourseButtonClick,
}: CourseListProps): React.ReactElement => {
  const [isOpened, setIsOpened] = useState(false);
  const deviceSize = useDeviceSize();

  const numberOfCompletedLessons = lessons.filter((lesson) => lesson.isCompleted).length;
  const calculateProgress = (): number => {
    if (course.courseStatus === CourseStatus.FINISHED) {
      return 100;
    }

    return (numberOfCompletedLessons / lessons.length) * 100;
  };

  const isCompleted = calculateProgress() === 100;
  const isTablet = deviceSize <= TABLET_SCREEN_WIDTH;

  const toggleLessons = () => {
    setIsOpened(!isOpened);
  };

  const getButtonText = (status: CourseStatus): string => {
    switch (status) {
      case CourseStatus.DEFAULT:
        return 'Get started';
      case CourseStatus.IN_PROGRESS:
        return 'Continue';
      case CourseStatus.FINISHED:
        return 'Review course';
      default:
        return 'Get started';
    }
  };

  const handleTitleClick = useCallback(
    (event?: React.MouseEvent): void => {
      if (onCourseTitleClick) {
        event?.preventDefault();
        onCourseTitleClick();
      }
    },
    [onCourseTitleClick],
  );

  const handleButtonClick = useCallback(
    (event?: React.MouseEvent): void => {
      if (onCourseButtonClick) {
        event?.preventDefault();
        onCourseButtonClick();
      }
    },
    [onCourseButtonClick],
  );

  return (
    <div
      css={[wrapperStyles, isOpened ? wrapperOpenStyles : wrapperClosedStyles, isCompleted && completedCourseStyles]}
      data-testid="course-list-container"
    >
      <div data-testid={`course-card-${course?.slug}`} css={containerStyles}>
        <div css={progressBadgeContainerStyles} data-testid="course-progress-badge">
          <div css={customProgressBadgeStyles}>
            {!isCompleted ? (
              <CircularProgressbarWithChildren
                strokeWidth={6}
                value={calculateProgress()}
                styles={buildStyles({
                  pathColor: token('color.border.success'),
                  trailColor: token('color.border'),
                })}
                data-testid="course-progress-circle"
              >
                <div css={imageBadgeContainerStyles}>
                  <img src={course.imageUrl} alt="Course badge" css={imageStyles} data-testid="course-badge-image" />
                </div>
              </CircularProgressbarWithChildren>
            ) : (
              <div css={completedBadgeContainerStyles} data-testid="course-completed-badge">
                <div css={imageBadgeContainerStyles}>
                  <img
                    src={course.imageUrl}
                    alt="Course badge"
                    css={[imageStyles, completedImageStyles]}
                    data-testid="course-completed-image"
                  />
                </div>
                <div css={checkmarkOverlayStyles}>
                  <img
                    src={svgCompletedGap}
                    alt="Completed"
                    css={checkmarkIconStyles}
                    data-testid="course-completed-checkmark"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div css={contentContainerStyles} data-testid="course-content">
          <div css={topContentStyles}>
            <div css={durationTitleContainerStyles}>
              <div css={durationTextStyles} data-testid="course-duration">
                {course.duration}
              </div>
              <Heading level={5}>
                <a css={titleStyles} href={course.href} onClick={handleTitleClick} data-testid="course-title-link">
                  {course.title}
                </a>
              </Heading>
            </div>
            {!isTablet && (
              <a
                css={startCourseButtonStyles}
                href={course.href}
                onClick={handleButtonClick}
                data-testid="desktop-course-button"
              >
                {getButtonText(course.courseStatus)}
              </a>
            )}
          </div>
          <div css={durationTitleContainerStyles}>
            <span css={subTitleStyles} data-testid="course-subtitle">
              {course.subTitle}
            </span>
          </div>
        </div>
      </div>
      {!isTablet && (
        <div
          css={[buttonContainerStyles, isOpened && buttonContainerOpenStyles, !isOpened && buttonContainerClosedStyles]}
        >
          <button css={toggleButtonStyles} onClick={toggleLessons} data-testid="toggle-lessons-button">
            {isOpened ? 'Hide lessons' : 'Show lessons'}
            <Icon size="small" type={isOpened ? 'chevron-up' : 'chevron-down'} color={token('color.text.brand')} />
          </button>
        </div>
      )}
      {isOpened && (
        <div css={[lessonsContentWrapperStyles, isTablet && lessonContentWrapperMobileStyles]}>
          <LessonsList lessons={lessons} onLessonClick={onLessonClick} isStandalone={false} isTablet={isTablet} />
        </div>
      )}
      {isTablet && (
        <div
          css={[
            buttonContainerStyles,
            isOpened && buttonContainerOpenStyles,
            !isOpened && buttonContainerClosedStyles,
            buttonContainerMobileStyles,
          ]}
        >
          <button
            css={[toggleButtonStyles, toggleButtonMobileStyles]}
            onClick={toggleLessons}
            data-testid="toggle-lessons-button-mobile"
          >
            {isOpened ? 'Hide lessons' : 'Show lessons'}
            <Icon size="small" type={isOpened ? 'chevron-up' : 'chevron-down'} color={token('color.text.brand')} />
          </button>
          <a
            css={continueButtonStyles}
            href={course.href}
            onClick={handleButtonClick}
            data-testid="tablet-course-button"
          >
            {getButtonText(course.courseStatus)}
          </a>
        </div>
      )}
    </div>
  );
};

const containerStyles = css({
  display: 'flex',
  padding: token('space.200'),
  alignItems: 'flex-start',
  gap: token('space.200'),
  alignSelf: 'stretch',
  minHeight: '120px',
  backgroundColor: token('color.background.input'),

  '&:hover a': {
    opacity: 1,
  },

  '@media (max-width: 343px)': {
    minHeight: '148px',
    padding: `${token('space.200')} ${token('space.100')}`,
    gap: token('space.100'),
  },
});

const progressBadgeContainerStyles = css({
  width: token('space.500'),
  height: '41px',
  flexShrink: 0,
});

const customProgressBadgeStyles = css({
  width: token('space.500'),
  height: '41px',
});

const imageBadgeContainerStyles = css({
  width: '100%',
  height: '100%',
  padding: token('space.100'),
});

const imageStyles = css({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  borderRadius: '50%',
});

const completedBadgeContainerStyles = css({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const completedImageStyles = css({
  opacity: 0.5,
  filter: 'grayscale(100%)',
});

const checkmarkOverlayStyles = css({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const checkmarkIconStyles = css({
  width: token('space.300'),
  height: token('space.300'),
});

const contentContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginRight: 'auto',
  flex: 1,
  overflow: 'hidden',
  minWidth: 0,
  width: '100%',
});

const topContentStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.150'),
  marginBottom: token('space.050'),
  minWidth: 0,
  width: '100%',
});

const durationTitleContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
  minWidth: 0,
  width: '100%',
});

const titleStyles = css({
  color: token('color.text'),
  fontFamily: 'Charlie Display',
  fontSize: '20px',
  fontStyle: 'normal',
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
  textDecoration: 'none',
  cursor: 'pointer',
  lineHeight: '28px',
  fontWeight: 600,
  width: '100%',
  minWidth: 0,
  display: 'block',
  maxHeight: '56px',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',

  '&:hover': {
    textDecoration: 'underline',
    color: token('color.text'),
  },

  '@media (max-width: 343px)': {
    fontSize: '16px',
    lineHeight: '24px',
    whiteSpace: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    maxHeight: '48px',
  },
});

const startCourseButtonStyles = css({
  color: token('color.text.brand'),
  fontFamily: 'Charlie Text',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '20px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: `${token('space.100')} ${token('space.150')}`,
  display: 'flex',
  alignItems: 'center',
  opacity: 0,
  transition: 'opacity 0.2s ease',
  textDecoration: 'none',

  '&:hover': {
    textDecoration: 'underline',
    textUnderlineOffset: token('space.050'),
    color: token('color.text.brand'),
  },

  '&:visited': {
    color: token('color.text.brand'),
  },
});

const subTitleStyles = css({
  color: token('color.text'),
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  fontFamily: 'Inter',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '20px',
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
  width: '100%',
  minWidth: 0,
  maxHeight: '40px',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',

  '@media (max-width: 343px)': {
    lineHeight: '22px',
    whiteSpace: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    maxHeight: '44px',
  },
});

const durationTextStyles = css({
  color: token('color.text.subtlest'),
  fontFamily: 'Charlie Text',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '16px',
  textTransform: 'uppercase',
  marginBottom: token('space.050'),
});

const buttonContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.200'),
  alignSelf: 'stretch',
  borderTop: `1px solid ${token('color.border')}`,
  padding: `${token('space.150')} ${token('space.300')}`,

  '@media (max-width: 343px)': {
    gap: token('space.100'),
    padding: `${token('space.150')} ${token('space.100')}`,
  },
});

const toggleButtonStyles = css({
  color: token('color.text.brand'),
  fontFamily: 'Charlie Text',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 600,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: token('space.0'),
  display: 'flex',
  alignItems: 'center',
  gap: token('space.050'),

  '&:hover': {
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
  },
});

const toggleButtonMobileStyles = css({
  color: token('color.text.brand'),
  textAlign: 'center',
  fontFamily: 'Charlie Text',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '20px',
});

const buttonContainerOpenStyles = css({
  borderBottom: `1px solid ${token('color.border')}`,
});

const wrapperStyles = css({
  border: `1px solid ${token('color.border')}`,
  overflow: 'hidden',
  maxWidth: '100%',
  backgroundColor: token('color.background.input'),
});

const wrapperClosedStyles = css({
  borderRadius: '8px',
});

const wrapperOpenStyles = css({
  borderRadius: '8px',

  '& > div:last-child': {
    borderRadius: '0 0 8px 8px',
  },
});

const lessonsContentWrapperStyles = css({
  overflow: 'hidden',
  borderRadius: '0 0 8px 8px !important',
  backgroundColor: '#FFFFFF',

  '& > div': {
    borderRadius: '0 0 8px 8px !important',
  },

  '& > div > div:last-child': {
    borderRadius: '0 0 8px 8px !important',
  },
});

const lessonContentWrapperMobileStyles = css({
  borderRadius: '0 !important',

  '& > div': {
    borderRadius: '0 !important',
  },
  '& > div > div:last-child': {
    borderRadius: '0 !important',
  },
});

const completedCourseStyles = css({
  borderLeft: `4px solid ${token('color.border.success')}`,
});

const buttonContainerClosedStyles = css({
  justifyContent: 'space-between',
});

const buttonContainerMobileStyles = css({
  padding: token('space.150'),
  justifyContent: 'space-between',
});

const continueButtonStyles = css({
  color: token('color.text.inverse'),
  backgroundColor: token('color.background.brand.bold'),
  fontFamily: 'Charlie Text',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '20px',
  height: '32px',
  borderRadius: '4px',
  border: 'none',
  cursor: 'pointer',
  padding: `${token('space.100')} ${token('space.150')}`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  '&:hover': {
    backgroundColor: token('color.background.brand.bold.hovered'),
    color: token('color.text.inverse'),
    textDecoration: 'none',
  },

  '&:active': {
    backgroundColor: token('color.background.brand.bold.pressed'),
  },
});
